import React, { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { graphql, navigate } from 'gatsby';
import Img from 'gatsby-image';

import { Grid, Row, Col, Divider } from '@components/Grid';
import Button from '@components/Button';
import { emptyBasket, setBasketItem } from '@actions/basket';
import {
    getBasket,
    getBasketSubtotal,
    getProductDiscountAccountType,
    getProductDiscountName,
} from '@helpers/store';

import styles from './styles.module.scss';
import { makePutRequest } from '@helpers/requests';
import { BASKET_ADD } from '@helpers/api';
import Layout from '@components/Layout';
import { useSnackbar } from '@components/Snackbar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import usePrevious from '@helpers/hooks/usePrevious';
import useClient from '@helpers/hooks/useClient';
import { getCustomField } from '@helpers/customField';
import Text from '@components/Text';
import SEO from '@components/SEO';
import Loader from '@components/Loader';

const Cart = ({
                    data: {
                        merlinWebsite: {
                            store: {
                                currency: { symbol: currencySymbol },
                            },
                        },
                        allMerlinStoreProduct: { nodes: products },
                        allMerlinAccountType: { nodes: accountTypes },
                    },
                }) => {
    const client = useClient();
    const basket = useSelector(({ basket }) => basket ?? {});
    const basketProducts = client ? getBasket(products, basket) : [];
    const subtotal = getBasketSubtotal(basketProducts);

    return (
        <Layout>
            <SEO title="Cart" />
            <Grid>
                <h1>Basket</h1>

                {!client ? (
                    <Loader />
                ) : !!basketProducts.length ? (
                    <Fragment>
                        <div className={styles.basketItems}>
                            {basketProducts.map(product => (
                                <BasketProduct
                                    key={product?.id}
                                    {...product}
                                    currencySymbol={currencySymbol}
                                    basketProducts={basketProducts}
                                    accountTypes={accountTypes}
                                    basket={basket}
                                />
                            ))}
                        </div>

                        <Divider />

                        <Row end="xs">
                            <Col xs={12}>
                                <div className={styles.basketSubtotal}>
                                    <span className={styles.basketSubtotalLabel}>Subtotal</span>
                                    <span className={styles.basketSubtotalValue}>
                                        {currencySymbol}
                                        {subtotal.toFixed(2)}
                                    </span>
                                </div>
                            </Col>
                        </Row>

                        <Divider />

                        <Row between="xs">
                            <Col>
                                <Button
                                    iconBefore
                                    secondary
                                    icon="arrow-left"
                                    text="Continue Shopping"
                                    link="/shop"
                                />
                                <Divider xsMargin={3} mdMargin={0} />
                            </Col>
                            <Col>
                                <Button
                                    iconBefore
                                    icon={['fal', 'shopping-basket']}
                                    text="Check Out"
                                    link="/checkout"
                                />
                            </Col>
                        </Row>
                    </Fragment>
                ) : (
                    <Fragment>
                        <p>You haven't added anything to your basket yet.</p>
                        <Divider />
                        <Button text="Go to store" link="/shop" />
                    </Fragment>
                )}
            </Grid>
        </Layout>
    );
};

const BasketProduct = product => {
    const {
        id,
        name,
        images,
        shortName,
        quantity: initialQuantity,
        variant,
        total,
        currencySymbol,
        basketProducts,
        basket,
        discount,
        price,
        accountTypes,
        customFields,
    } = product;
    const dispatch = useDispatch();
    const [openSnackbar] = useSnackbar();
    const [quantity, setQuantity] = useState(initialQuantity);
    const prevQuantity = usePrevious(quantity);
    const discountName = getProductDiscountName({ quantity, price: product?.price, discount });
    const discountAccountType = getProductDiscountAccountType({ accountTypes, discount });
    const ffl = getCustomField(customFields, 'ffl');

    useEffect(() => {
        if (typeof prevQuantity !== 'number' || prevQuantity === quantity) return;
        quantity > prevQuantity ? handleAddToBasket() : handleRemoveFromBasket();
    }, [quantity]);

    const handleRemoveFromBasket = async () => {
        try {
            dispatch(setBasketItem({ id, quantity }));

            // no quantity passed, remove all quantity
            const { data: basketData } = await makePutRequest(BASKET_ADD(basket.id), {
                productId: id,
                quantity,
                remove: !quantity,
            });

            const newQuantity =
                basketData?.products?.find(({ productId }) => productId === id)?.quantity ??
                quantity;

            if (quantity !== newQuantity) {
                dispatch(setBasketItem({ id, quantity: newQuantity }));

                // if quantity is truthy (not 0) then set, otherwise this would cause state errors as the component
                // is removed when quantity reaches 0.
                if (!!newQuantity) {
                    setQuantity(newQuantity);
                }
            }
        } catch (error) {
            if (error !== 'cancelled') {
                openSnackbar(
                    error?.errorMessage ?? 'An error occurred removing this item to your basket'
                );

                dispatch(emptyBasket());
            }
        }
    };

    const handleAddToBasket = async () => {
        try {
            dispatch(setBasketItem({ id, quantity }));

            const { data: basketData } = await makePutRequest(BASKET_ADD(basket.id), {
                productId: id,
                quantity,
            });

            const newQuantity =
                basketData?.products?.find(({ productId }) => productId === id)?.quantity ??
                quantity;

            if (quantity !== newQuantity) {
                dispatch(setBasketItem({ id, quantity: newQuantity }));

                if (!!newQuantity) {
                    // update quantity to ensure it's the same as API
                    setQuantity(newQuantity);
                }
            }
        } catch (error) {
            if (error !== 'cancelled') {
                openSnackbar(
                    error?.errorMessage ?? 'An error occurred adding this item to your basket'
                );
                dispatch(emptyBasket());
            }
        }
    };

    if (!quantity) return null;

    return (
        <div key={id} className={styles.basketItem}>
            <div className={styles.basketItemLeft}>
                <div
                    className={styles.basketItemImage}
                    onClick={() => navigate(`/product/${shortName}`)}
                >
                    {!!images && !!images[0] && !!images[0].image && (
                        <Img
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                bottom: 0,
                                right: 0,
                            }}
                            fluid={images[0].image.childImageSharp.fluid}
                        />
                    )}
                </div>

                <div className={styles.basketItemMeta}>
                    <p
                        onClick={() => navigate(`/product/${shortName}`)}
                        className={styles.basketItemMetaName}
                    >
                        {name}
                    </p>
                    {ffl?.value && (
                        <div className={styles.basketItemMetaFfl}>
                            <Text status="warning">
                                This item must be collected from an FFL dealer.
                            </Text>
                        </div>
                    )}
                    {!!variant && (
                        <span className={styles.basketItemMetaVariant}>{variant.name}</span>
                    )}
                    {!!discount && (
                        <p className={styles.basketItemMetaDiscount}>
                            <span className={styles.basketItemMetaDiscountName}>
                                {discountName}
                            </span>
                            {!!discountAccountType && (
                                <span className={styles.basketItemMetaDiscountAccountType}>
                                    {`${discountAccountType?.name} Discount`}
                                </span>
                            )}
                        </p>
                    )}
                    <p className={styles.basketItemPriceMobile}>
                        {!!discount ? (
                            <Fragment>
                                <span className={styles.basketItemPriceMobileDiscounted}>
                                    {currencySymbol}
                                    {(total / 100).toFixed(2)}
                                </span>
                                <span className={styles.basketItemPriceMobileDiscount}>
                                    {currencySymbol}
                                    {(discount?.total / 100).toFixed(2)}
                                </span>
                            </Fragment>
                        ) : (
                            `${currencySymbol}${(total / 100).toFixed(2)}`
                        )}
                    </p>
                </div>
            </div>

            <div className={styles.basketItemRight}>
                <ul className={styles.basketItemQuantity}>
                    <li
                        className={styles.basketItemQuantityAction}
                        onClick={() => setQuantity(quantity - 1)}
                    >
                        <FontAwesomeIcon icon="minus" />
                    </li>
                    <li className={styles.basketItemQuantityNumber}>{quantity}</li>
                    <li
                        className={styles.basketItemQuantityAction}
                        onClick={() => setQuantity(quantity + 1)}
                    >
                        <FontAwesomeIcon icon="plus" />
                    </li>
                </ul>

                <p className={styles.basketItemPrice}>
                    {!!discount ? (
                        <Fragment>
                            <span className={styles.basketItemPriceDiscounted}>
                                {currencySymbol}
                                {(total / 100).toFixed(2)}
                            </span>
                            <span className={styles.basketItemPriceDiscount}>
                                {currencySymbol}
                                {(discount?.total / 100).toFixed(2)}
                            </span>
                        </Fragment>
                    ) : (
                        `${currencySymbol}${(total / 100).toFixed(2)}`
                    )}
                </p>

                <span className={styles.basketItemRemove} onClick={() => setQuantity(0)}>
                    <FontAwesomeIcon icon="times" />
                </span>
            </div>
        </div>
    );
};

export default Cart;

export const query = graphql`
    query {
        merlinWebsite {
            store {
                currency {
                    symbol
                }
            }
        }
        allMerlinStoreProduct {
            nodes {
                _id
                shortName
                name
                price
                images {
                    image: url {
                        childImageSharp {
                            fluid(maxHeight: 300) {
                                ...GatsbyImageSharpFluid
                            }
                        }
                    }
                }
                discounts {
                    _id
                    type
                    code
                    type
                    amount
                    startDate
                    endDate
                    limit
                    products
                    categories
                    accountTypes
                }
                customFields {
                    _id
                    type
                    label
                    id
                    multiple
                    value {
                        switch
                    }
                }
            }
        }
        allMerlinAccountType {
            nodes {
                _id
                merlinId
                name
            }
        }
    }
`;
